import { Controller } from "@hotwired/stimulus";
import { markAlreadyPaid, markPaymentFailed } from "../../../sessionCompletion";
import { debugError, debugLog } from "../../../utils";
import { showErrorCard } from "../../../errorsurfaces";

// Progress updates mount a short-lived controller on #blockchain_progess_updater.
// Guard the success fade at module scope so two instances cannot both run it.
let blockchainSuccessTransitionStarted = false;

export default class BlockchainProgressController extends Controller {
  static values = {
    percentage: Number,
    text: String,
    transactionHash: String,
    transactionExplorerUrl: String,
    transactionStatus: String,
    hideTxDetails: Boolean,
    onrampCheckout: Boolean,
    isPosPayment: Boolean,
    errorKey: String,
  }

  connect() {
    this.outcomeAdvanceInFlight = false;
    this.lastAdvancedResult = null;
    this.successTransitionStarted = false;
    this.successRowsRevealed = false;

    // New checkout progress (not yet complete) must be allowed to animate again
    // in this Turbo session — the module guard survives Turbo navigations.
    if (this.hasPercentageValue && this.percentageValue < 100) {
      blockchainSuccessTransitionStarted = false;
    }

    if (this.isDedicatedOutcomePage()) {
      if (window.location.pathname.includes("/payment-failure")) {
        markPaymentFailed();
      } else {
        markAlreadyPaid();
      }
    }

    this.hideLoadingScreen();

    const checkoutTotalAmount = document.getElementById("checkout_total_amount");
    const checkoutPaymentMethod = document.getElementById("checkout_payment_method");

    // Explicit crypto-onramp signal. Prefer this render's own value (passed by
    // the progress/outcome handler, which always knows the flow); fall back to
    // the marker on the persistent order-summary element. When set we force the
    // payment method to "USD", hide all chain-specific UI, and stamp the marker
    // back onto the order-summary element so later Pusher-update controller
    // instances (which lack their own value) inherit it — no network/hash flash.
    const onrampCheckout =
      (this.hasOnrampCheckoutValue && this.onrampCheckoutValue) ||
      checkoutPaymentMethod?.dataset?.onrampCheckout === "1";
    if (onrampCheckout && checkoutPaymentMethod) {
      checkoutPaymentMethod.textContent = "USD";
      checkoutPaymentMethod.dataset.onrampCheckout = "1";
    }

    const checkoutPaymentMethodText = checkoutPaymentMethod?.textContent?.trim() || "";
    const checkoutTotalAmountText = checkoutTotalAmount?.textContent?.trim() || "";

    const paymentMethodParts = checkoutPaymentMethodText.split(" / ").map((v) => v.trim()).filter(Boolean);
    const totalAmountParts = checkoutTotalAmountText.split(/\s+/).map((v) => v.trim()).filter(Boolean);
    const hasExplicitPaymentMethod = checkoutPaymentMethodText !== "" && checkoutPaymentMethodText !== "Awaiting Selection";

    // "Polygon / USD" has a network part (normal fast-track / MetaMask flows); a
    // custodial coinsub-wallet (crypto-onramp) checkout shows just "USD".
    const hasNetworkPart = hasExplicitPaymentMethod && paymentMethodParts.length >= 2;
    // Hide chain UI for the crypto-onramp flow (explicit flag), or when the
    // payment method is explicitly network-less. An unset / "Awaiting Selection"
    // method is left alone, so normal crypto flows always keep their network +
    // tx-hash display.
    this.hideOnChainDetails = onrampCheckout || (hasExplicitPaymentMethod && !hasNetworkPart);

    const checkoutPrice = totalAmountParts[0] || "";
    const checkoutCurrency =
      (hasNetworkPart ? paymentMethodParts[1] : paymentMethodParts[0]) ||
      totalAmountParts[1] ||
      "USD";
    const checkoutNetwork = hasNetworkPart ? paymentMethodParts[0] : "";

    const checkoutTotalPrice = document.getElementById("checkout_total_price");
    const checkoutCurrencyEl = document.getElementById("checkout_currency");
    const checkoutNetworkEl = document.getElementById("checkout_network");
    const checkoutNetworkRow = document.getElementById("checkout_network_row");

    // Prefer values already rendered by the server on /payment-success. Only
    // overwrite when the overview parse produced a value — never clear a
    // server-rendered network with an empty string.
    if (checkoutTotalPrice && checkoutPrice) {
      checkoutTotalPrice.textContent = checkoutPrice;
    }
    if (checkoutCurrencyEl) {
      const existingCurrency = checkoutCurrencyEl.textContent?.trim() || "";
      if (checkoutCurrency || !existingCurrency) {
        checkoutCurrencyEl.textContent = checkoutCurrency || existingCurrency;
      }
    }
    if (checkoutNetworkEl) {
      const existingNetwork = checkoutNetworkEl.textContent?.trim() || "";
      const networkToShow = checkoutNetwork || existingNetwork;
      if (networkToShow) {
        checkoutNetworkEl.textContent = networkToShow;
      }
    }
    if (checkoutNetworkRow) {
      const networkVisible =
        !this.hideOnChainDetails &&
        Boolean(checkoutNetwork || checkoutNetworkEl?.textContent?.trim());
      checkoutNetworkRow.classList.toggle("hidden", !networkVisible);
    }
    this.updateProgressLabelsOpacity();
    this.updateTransactionDetails();

    if (this.hasPercentageValue) {
      this.update();
    }
  }

  disconnect() {
    this.clearCompletedFadeOutTimeout();
    // Do not clear success-transition timers here. Progress updates mount a
    // short-lived controller on #blockchain_progess_updater; Turbo can
    // disconnect it while the fade is in flight. Callbacks use document
    // getElementById, so they remain valid after this instance disconnects.
  }

  update() {
    debugLog("Update State:", this.percentageValue);

    const progressText = document.getElementById("blockchain_progress_text");
    const progressBarProcess = document.getElementById("blockchain_progress_bar");

    if (!progressBarProcess || !progressText) {
      debugError("BlockchainProgressController: update() - progressBarProcess or progressText not found");
      return
    }

    // Update the progress state.
    progressText.textContent = this.textValue;
    progressBarProcess.style.width = `${this.percentageValue}%`;
    this.updateProgressLabelsOpacity();
    this.updateTransactionDetails();

    // Show failure UI
    if (this.percentageValue === 0) {
      this.showFailureState();
    }

    // Show success UI
    if (this.percentageValue === 100) {
      this.showSuccessState();
    }
  }

  showSuccessState() {
    if (this.successTransitionStarted) {
      return;
    }
    this.successTransitionStarted = true;

    // In-progress page: navigate to /payment-success without a local success fade.
    // The dedicated outcome page owns the spinner → completed animation so we
    // don't get a double fade (handoff + Turbo frame transition).
    if (!this.isDedicatedOutcomePage()) {
      if (blockchainSuccessTransitionStarted) {
        return;
      }
      blockchainSuccessTransitionStarted = true;
      this.advanceToOutcomeIfNeeded("success");
      return;
    }

    // /payment-success: fade out progress, then fade in final success content.
    const finishSuccessUI = () => {
      this.hideProgressUI();
      this.successRowsRevealed = true;
      this.updateTransactionDetails();
      this.showFinalSuccessContent();
    };

    setTimeout(() => {
      this.fadeOutProgressUI();
      setTimeout(finishSuccessUI, 300);
    }, 300);
  }

  showFailureState() {
    const blockchainProgressIndicator = document.getElementById("blockchain_progress_indicator");
    const blockchainFailedIconCircle = document.getElementById("blockchain_failed_icon_circle");
    const blockchainProgressSpinnerWrap = document.getElementById("blockchain_progress_spinner_wrap");
    const blockChainFeedbackHeader = document.getElementById("blockchain_feedback_header");
    const blockchainFeedbackMessage = document.getElementById("blockchain_feedback_message");
    const progressIconCircle = document.getElementById("blockchain_progress_icon_circle");
    const transactionStatus = document.getElementById("checkout_tx_status");

    if (!blockchainProgressIndicator || !blockchainFailedIconCircle || !blockChainFeedbackHeader || !blockchainFeedbackMessage) {
      return;
    }

    if (blockchainProgressSpinnerWrap) {
      blockchainProgressSpinnerWrap.classList.add("hidden");
      blockchainProgressSpinnerWrap.classList.remove("flex");
    }

    if (progressIconCircle) {
      progressIconCircle.classList.add("hidden");
      progressIconCircle.classList.remove("flex");
    }

    blockchainFailedIconCircle.classList.remove("hidden");
    blockchainFailedIconCircle.classList.add("flex");

    blockchainProgressIndicator.classList.add("hidden");

    blockChainFeedbackHeader.classList.remove("text-thm-body-primary-text", "text-primaryColor", "text-thm-body-success-text-and-icon");
    blockChainFeedbackHeader.classList.add("text-thm-body-error-text-and-icon-color");
    blockChainFeedbackHeader.textContent = this.hasTextValue && this.textValue ? this.textValue : "Payment Failed!";

    blockchainFeedbackMessage.textContent = "Something went wrong while processing your payment. Please try again in a few moments.";
    this.ensureStartOverButton();
    this.showErrorSurface();

    blockchainFailedIconCircle.classList.remove("border-2", "border-primaryColor");
    blockchainFailedIconCircle.classList.add("bg-thm-red");

    // Failure path reveals status immediately (no success fade).
    this.successRowsRevealed = true;
    this.updateTransactionDetails();

    if (transactionStatus) {
      transactionStatus.classList.remove("text-thm-body-primary-text", "text-thm-body-success-text-and-icon");
      transactionStatus.classList.add("text-thm-body-error-text-and-icon-color");
      transactionStatus.textContent = "Failed";
    }

    this.advanceToOutcomeIfNeeded("failure");
  }

  // Renders the catalog error surface (retry card / needs-help / status) for
  // the pushed error key over the failed progress screen.
  showErrorSurface() {
    if (!this.hasErrorKeyValue || !this.errorKeyValue) {
      return;
    }
    showErrorCard(this.errorKeyValue);
  }

  fadeOutProgressUI() {
    const progressContainer = document.getElementById("blockchain_progress_container");
    const summaryDetails = document.getElementById("blockchain_summary_details");

    if (progressContainer) {
      progressContainer.style.opacity = "0";
      progressContainer.style.transform = "translateY(-20px)";
    }

    if (summaryDetails) {
      summaryDetails.style.opacity = "0";
      summaryDetails.style.transform = "translateY(-20px)";
    }
  }

  hideProgressUI() {
    const progressContainer = document.getElementById("blockchain_progress_container");
    if (progressContainer) {
      progressContainer.classList.add("hidden");
    }
  }

  showFinalSuccessContent() {
    const finalContent = document.getElementById("blockchain_final_content");
    const summaryDetails = document.getElementById("blockchain_summary_details");
    const onrampNote = document.getElementById("blockchain_onramp_statement_note");

    if (finalContent) {
      finalContent.classList.remove("hidden");
      void finalContent.offsetHeight;
      finalContent.style.opacity = "1";
      finalContent.style.transform = "translateY(0px)";
    }

    if (summaryDetails) {
      void summaryDetails.offsetHeight;
      summaryDetails.style.opacity = "1";
      summaryDetails.style.transform = "translateY(0px)";
    }

    if (onrampNote) {
      onrampNote.classList.remove("hidden");
      void onrampNote.offsetHeight;
      onrampNote.style.opacity = "1";
      onrampNote.style.transform = "translateY(0px)";
    }
  }

  async advanceToOutcomeIfNeeded(result) {
    if (this.isDedicatedOutcomePage()) {
      return;
    }
    if (this.outcomeAdvanceInFlight || this.lastAdvancedResult === result) {
      return;
    }

    const payload = this.buildOutcomeAdvancePayload(result);
    if (!payload.session || !payload.code) {
      return;
    }

    this.outcomeAdvanceInFlight = true;
    try {
      const response = await fetch(`/${payload.code}/payment-outcome/advance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const resolvedURL = this.getResolvedURL(data?.url);
      if (!resolvedURL) {
        return;
      }

      this.lastAdvancedResult = result;
      if (data?.external) {
        if (result === "failure") {
          markPaymentFailed();
        } else {
          markAlreadyPaid();
        }
        window.location.assign(resolvedURL.toString());
        return;
      }

      // Frame visit keeps the checkout overview (payment method / network) mounted,
      // matching direct-deposit success. Full-page visit would re-render overview
      // from session and can leave Network blank if the DOM parse races.
      Turbo.visit(resolvedURL.pathname + resolvedURL.search, {
        frame: "step_content",
        action: "advance",
      });
    } catch (error) {
      debugError("BlockchainProgressController: outcome advance failed", error);
    } finally {
      this.outcomeAdvanceInFlight = false;
    }
  }

  buildOutcomeAdvancePayload(result) {
    const url = new URL(window.location.href);
    const pathSegments = url.pathname.split("/").filter(Boolean);
    const code = pathSegments[0] || "";
    const session = url.searchParams.get("session") || "";

    return {
      code,
      session,
      result,
      text: this.hasTextValue ? this.textValue : "",
      transactionHash: this.hasTransactionHashValue ? this.transactionHashValue : "",
      transactionExplorerUrl: this.hasTransactionExplorerUrlValue ? this.transactionExplorerUrlValue : "",
      transactionStatus: this.hasTransactionStatusValue ? this.transactionStatusValue : "",
      errorKey: this.hasErrorKeyValue ? this.errorKeyValue : "",
    };
  }

  isDedicatedOutcomePage() {
    return window.location.pathname.includes("/payment-success") || window.location.pathname.includes("/payment-failure");
  }

  getResolvedURL(rawURL) {
    if (!rawURL || typeof rawURL !== "string") {
      return null;
    }

    try {
      const normalized = !/^https?:\/\//i.test(rawURL) && !rawURL.startsWith("/")
        ? `https://${rawURL}`
        : rawURL;
      const resolvedURL = new URL(normalized, window.location.origin);
      if (resolvedURL.protocol !== "http:" && resolvedURL.protocol !== "https:") {
        return null;
      }
      return resolvedURL;
    } catch {
      return null;
    }
  }

  ensureStartOverButton() {
    const blockchainFeedbackMessage = document.getElementById("blockchain_feedback_message");
    if (!blockchainFeedbackMessage) {
      return;
    }

    let startOverButton = document.getElementById("blockchain_start_over_button");
    if (!startOverButton) {
      startOverButton = document.createElement("a");
      startOverButton.id = "blockchain_start_over_button";
      startOverButton.className = "mb-4 inline-flex items-center justify-center rounded-lg bg-primaryColor px-4 py-2 text-sm font-medium text-thm-button-primary-label-color";
      startOverButton.textContent = "Start Over";
      startOverButton.setAttribute("data-turbo", "false");
      startOverButton.setAttribute("data-turbo-frame", "_top");
      blockchainFeedbackMessage.insertAdjacentElement("afterend", startOverButton);
    }

    const startOverUrl = this.getStartOverUrl();
    if (startOverUrl) {
      startOverButton.setAttribute("href", startOverUrl);
    }
  }

  getStartOverUrl() {
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    if (pathSegments.length === 0) {
      return null;
    }

    const code = pathSegments[0];
    return `${window.location.origin}/${code}/`;
  }

  updateProgressLabelsOpacity() {
    const progressText = document.getElementById("blockchain_progress_text");
    const detectedLabel = progressText?.previousElementSibling;
    const completedLabel = progressText?.nextElementSibling;

    if (!progressText || !detectedLabel || !completedLabel) {
      return;
    }

    const progress = this.getClampedProgress();
    const detectedOpacity = progress <= 65 ? 1 - progress / 65 : 0;
    const confirmingOpacity = this.getConfirmingOpacity(progress);
    const completedOpacity = progress <= 35 ? 0 : (progress - 35) / 65;
    const defaultOpacityTransition = "opacity 450ms cubic-bezier(0.22, 1, 0.36, 1)";

    detectedLabel.style.transition = defaultOpacityTransition;
    progressText.style.transition = defaultOpacityTransition;
    completedLabel.style.transition = defaultOpacityTransition;

    detectedLabel.style.opacity = `${detectedOpacity}`;
    progressText.style.opacity = `${confirmingOpacity}`;

    if (progress === 100) {
      this.triggerCompletedLabelFadeOut(completedLabel);
      return;
    }

    this.completedFadeOutTriggered = false;
    this.clearCompletedFadeOutTimeout();
    completedLabel.style.opacity = `${completedOpacity}`;
  }

  getClampedProgress() {
    if (!this.hasPercentageValue || Number.isNaN(this.percentageValue)) {
      return 0;
    }

    return Math.min(100, Math.max(0, this.percentageValue));
  }

  getConfirmingOpacity(progress) {
    if (progress <= 45) {
      return progress / 45;
    }

    if (progress <= 55) {
      return 1;
    }

    return (100 - progress) / 45;
  }

  triggerCompletedLabelFadeOut(completedLabel) {
    if (this.completedFadeOutTriggered) {
      return;
    }

    this.completedFadeOutTriggered = true;
    this.clearCompletedFadeOutTimeout();
    completedLabel.style.opacity = "1";
    completedLabel.style.transition = "opacity 3000ms cubic-bezier(0.16, 1, 0.3, 1)";
    this.completedFadeOutTimeout = window.setTimeout(() => {
      completedLabel.style.opacity = "0";
      this.completedFadeOutTimeout = null;
    }, 120);
  }

  clearCompletedFadeOutTimeout() {
    if (this.completedFadeOutTimeout) {
      window.clearTimeout(this.completedFadeOutTimeout);
      this.completedFadeOutTimeout = null;
    }
  }

  updateTransactionDetails() {
    // POS confirmation renders a POS-tailored receipt (Amount, Order ID,
    // Merchant, TX Hash, Date) — the TX Hash row is provided by the template so
    // it sits above the Date row, and there is NO Status row. Read the marker off
    // the summary element too, not just this instance's value: the Pusher
    // progress-update stream spawns its own controller instance without the value
    // (same reason the onramp marker is mirrored onto the DOM above), and it must
    // treat the receipt as POS just like the outcome-page instance.
    const summaryContainer = document.getElementById("blockchain_summary_details");
    const isPosReceipt =
      (this.hasIsPosPaymentValue && this.isPosPaymentValue) ||
      summaryContainer?.dataset?.posReceipt === "1";
    const txNodes = this.ensureTransactionDetailsNodes(isPosReceipt);
    if (!txNodes) {
      return;
    }
    const { txHashRow, transactionHashText, transactionHashLink, transactionStatus } = txNodes;

    const hash = this.hasTransactionHashValue ? this.transactionHashValue.trim() : "";
    const explorerUrl = this.hasTransactionExplorerUrlValue ? this.transactionExplorerUrlValue.trim() : "";
    const status = this.hasTransactionStatusValue ? this.transactionStatusValue.trim() : "";
    const statusLower = status.toLowerCase();
    const isSuccessful = this.percentageValue === 100 || statusLower === "completed" || statusLower === "success";

    // Crypto-onramp checkout pays from the custodial coinsub wallet, so the
    // on-chain tx hash is noise — keep the row hidden for that flow. Driven by
    // the same "no network part" signal as the Network row (hideOnChainDetails),
    // so every progress/outcome render — including the Pusher 100% update that
    // carries the hash — keeps it hidden (no brief flash before the final reload).
    const hideTxDetails =
      this.hideOnChainDetails || (this.hasHideTxDetailsValue && this.hideTxDetailsValue);
    const isFailed = this.percentageValue === 0 || statusLower === "failed";
    // Success rows wait for the fade handoff (successRowsRevealed). Failure shows immediately.
    const shouldShowTxRow =
      !hideTxDetails && ((isSuccessful && this.successRowsRevealed) || (isFailed && Boolean(hash)));
    const shouldShowStatusRow = (isSuccessful && this.successRowsRevealed) || isFailed;

    if (txHashRow) {
      txHashRow.classList.toggle("hidden", !shouldShowTxRow);
    }

    if (transactionStatus?.parentElement) {
      transactionStatus.parentElement.classList.toggle("hidden", !shouldShowStatusRow);
    }

    if (hash) {
      const shortHash = hash.length > 12 ? `${hash.slice(0, 4)}...${hash.slice(-4)}` : hash;
      transactionHashText.textContent = shortHash;
      transactionHashLink.title = hash;
      if (explorerUrl) {
        transactionHashLink.href = explorerUrl;
        transactionHashLink.setAttribute("target", "_blank");
      } else {
        transactionHashLink.removeAttribute("href");
        transactionHashLink.removeAttribute("target");
      }
    } else {
      transactionHashText.textContent = "-";
      transactionHashLink.removeAttribute("title");
      transactionHashLink.removeAttribute("href");
      transactionHashLink.removeAttribute("target");
    }

    if (status && transactionStatus) {
      transactionStatus.textContent = status;
      transactionStatus.classList.remove("text-thm-body-error-text-and-icon-color", "text-thm-body-success-text-and-icon");
      if (statusLower === "completed" || statusLower === "success") {
        transactionStatus.classList.remove("text-thm-body-primary-text");
        transactionStatus.classList.add("text-thm-body-success-text-and-icon");
      }
      if (statusLower === "failed") {
        transactionStatus.classList.remove("text-thm-body-primary-text");
        transactionStatus.classList.add("text-thm-body-error-text-and-icon-color");
      }
    }
  }

  ensureTransactionDetailsNodes(isPosReceipt = false) {
    const summaryContainer = document.getElementById("blockchain_summary_details");
    if (!summaryContainer) {
      return null;
    }

    let transactionHashText = document.getElementById("checkout_tx_hash_text");
    let transactionHashLink = document.getElementById("checkout_tx_hash_link");
    let transactionStatus = document.getElementById("checkout_tx_status");
    let txHashRow = document.getElementById("checkout_tx_hash_row");

    // POS receipt: the TX Hash row lives in the template (above Date) and there
    // is no Status row. Reuse the template nodes as-is; never inject.
    if (isPosReceipt) {
      if (transactionHashText && transactionHashLink && txHashRow) {
        return { txHashRow, transactionHashText, transactionHashLink, transactionStatus: null };
      }
      return null;
    }

    if (transactionHashText && transactionHashLink && transactionStatus && txHashRow) {
      return { txHashRow, transactionHashText, transactionHashLink, transactionStatus };
    }

    const baseLabelClasses = "text-sm text-thm-body-primary-text";
    const baseValueClasses = "text-sm font-medium text-thm-body-primary-text";
    const dividerClasses = "hidden flex justify-between items-center py-3 border-t border-thm-desktop-sidebar-border-color";

    txHashRow = document.createElement("div");
    txHashRow.id = "checkout_tx_hash_row";
    txHashRow.className = dividerClasses;
    const txHashLabel = document.createElement("span");
    txHashLabel.className = baseLabelClasses;
    txHashLabel.textContent = "TX Hash";
    transactionHashLink = document.createElement("a");
    transactionHashLink.id = "checkout_tx_hash_link";
    transactionHashLink.className = "text-sm font-medium text-primaryColor";
    transactionHashLink.rel = "noopener noreferrer";
    transactionHashText = document.createElement("span");
    transactionHashText.id = "checkout_tx_hash_text";
    transactionHashText.textContent = "";
    transactionHashLink.appendChild(transactionHashText);
    txHashRow.append(txHashLabel, transactionHashLink);

    const statusRow = document.createElement("div");
    statusRow.className = "hidden flex justify-between items-center py-3 border-t border-thm-desktop-sidebar-border-color";
    const statusLabel = document.createElement("span");
    statusLabel.className = baseLabelClasses;
    statusLabel.textContent = "Status";
    transactionStatus = document.createElement("span");
    transactionStatus.id = "checkout_tx_status";
    transactionStatus.className = baseValueClasses;
    transactionStatus.textContent = "Pending";
    statusRow.append(statusLabel, transactionStatus);

    summaryContainer.append(txHashRow, statusRow);
    return { txHashRow, transactionHashText, transactionHashLink, transactionStatus };
  }

  showLoadingScreen() {
    this.dispatch("showLoadingScreen", { detail: { text: "Please Wait" } });
  }

  hideLoadingScreen() {
    this.dispatch("hideLoadingScreen");
  }
}
